import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  XCircle, 
  Zap
} from 'lucide-react';
import { auditService } from '@/services/audit';
import { AuditForm, AuditProgress, IssuesList, FixProgress } from './components';

interface Audit {
  id: number;
  audit_id: string;
  website_url: string;
  website_name?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  organization: number;
  project: number;
  created_at: string;
  updated_at: string;
  audit_data?: any;
}

interface Issue {
  id: number;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string;
  url: string;
  element: string;
  recommendation: string;
  status: 'pending' | 'fixed' | 'ignored';
  created_at: string;
  updated_at?: string;
}

const AIAudit: React.FC = () => {
  const { auditId } = useParams<{ auditId?: string }>();
  const [currentAudit, setCurrentAudit] = useState<Audit | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [fixProgress, setFixProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showIssues, setShowIssues] = useState(false);

  // Load existing audit data when auditId is provided
  useEffect(() => {
    const loadAuditData = async () => {
      if (auditId) {
        try {
          setIsLoading(true);
          setError(null);
          
          // Load audit details
          const audit = await auditService.getAudit(auditId);
          setCurrentAudit(audit);
          
          // Load issues if audit is completed
          if (audit.status === 'completed') {
            const auditIssuesResponse = await auditService.getAuditIssues(auditId);
            setIssues(auditIssuesResponse.results);
            setShowIssues(true);
          }
        } catch (error) {
          console.error('Failed to load audit data:', error);
          setError('Failed to load audit data');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadAuditData();
  }, [auditId]);

  const handleStartAudit = async (url: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const audit = await auditService.createAudit({ 
        website_url: url, 
        organization: 1, // Default organization
        project: 1 // Default project
      });
      setCurrentAudit(audit);
      setShowIssues(true);
    } catch (error) {
      console.error('Failed to start audit:', error);
      setError('Failed to start audit');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFixAll = async () => {
    if (currentAudit?.audit_id && issues.length > 0) {
      try {
        setIsFixing(true);
        setFixProgress(0);
        await auditService.fixAllIssues(currentAudit.audit_id);
        
        // Simulate progress updates
        const interval = setInterval(() => {
          setFixProgress(prev => {
            if (prev >= 100) {
              clearInterval(interval);
              setIsFixing(false);
              return 100;
            }
            return prev + 10;
          });
        }, 500);
        
        // Update all issues to fixed status
        setIssues(prev => prev.map(issue => ({ ...issue, status: 'fixed' as const })));
      } catch (error) {
        console.error('Failed to fix all issues:', error);
        setError('Failed to fix all issues');
        setIsFixing(false);
      }
    }
  };

  const handleFixSingle = async (issueId: number) => {
    try {
      await auditService.fixSingleIssue(issueId);
      setIssues(prev => prev.map(issue => 
        issue.id === issueId ? { ...issue, status: 'fixed' as const } : issue
      ));
    } catch (error) {
      console.error('Failed to fix issue:', error);
      setError('Failed to fix issue');
    }
  };


  return (
    <div className="flex gap-6">
      {/* Main Content */}
      <div className="flex-1 space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-muted-primary">AI Website Audit</h1>
          <p className="text-muted-foreground">
            Get comprehensive insights and AI-powered fixes for your website
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
            {/* Audit Form */}
            {!auditId && !currentAudit && (
              <AuditForm 
                onStartAudit={handleStartAudit}
                isLoading={isLoading}
              />
            )}

            {/* Audit Progress */}
            {currentAudit && currentAudit.status !== 'completed' && (
              <Card className="p-6">
                <AuditProgress 
                  audit={currentAudit}
                  onComplete={(fetchedIssues: Issue[]) => {
                    setIssues(fetchedIssues);
                    setShowIssues(true);
                  }}
                />
              </Card>
            )}

            {/* Issues List */}
            {showIssues && issues.length > 0 && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold mb-2">
                      Found {issues.length} Issues
                    </h2>
                    <p className="text-muted-foreground">
                      Review and fix issues to improve your website
                    </p>
                  </div>
                  <Button 
                    onClick={handleFixAll}
                    disabled={isFixing}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Fix All with AI
                  </Button>
                </div>

                <IssuesList 
                  issues={issues}
                  onFixSingle={handleFixSingle}
                  isFixing={isFixing}
                />
              </Card>
            )}

            {/* Fix Progress */}
            {isFixing && (
              <Card className="p-6">
                <FixProgress 
                  progress={fixProgress}
                  totalIssues={issues.length}
                />
              </Card>
            )}

            {/* No Issues Found */}
            {showIssues && issues.length === 0 && (
              <Card className="p-6 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Issues Found!</h3>
                <p className="text-muted-foreground">
                  Your website is in great shape. Great job!
                </p>
              </Card>
            )}

            {/* Error State */}
            {error && (
              <Card className="p-6 border-red-200 bg-red-50">
                <div className="flex items-center">
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  <div>
                    <h3 className="font-semibold text-red-800">Error</h3>
                    <p className="text-red-600">{error}</p>
                  </div>
                </div>
              </Card>
            )}
        </div>
      </div>
    </div>
  );
};

export default AIAudit;
